// Global error tracker for Apollo Client (non-React context)
// This allows Apollo Client to track errors without React dependencies

type ErrorCallback = (failures: number) => void;

class ErrorTracker {
  private consecutiveFailures = 0;
  private lastSuccessTime = Date.now();
  private lastActivityTime = Date.now();
  private listeners: ErrorCallback[] = [];
  private failureThreshold = 3;
  private activityListeners: (() => void)[] = [];

  constructor() {
    // Set up activity tracking
    this.setupActivityTracking();
  }

  private setupActivityTracking() {
    if (typeof window === 'undefined') return;
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach((event) => {
      const handler = () => {
        this.lastActivityTime = Date.now();
      };
      window.addEventListener(event, handler, { passive: true });
      this.activityListeners.push(() => {
        window.removeEventListener(event, handler);
      });
    });
  }

  recordFailure() {
    this.consecutiveFailures += 1;
    this.notifyListeners();
  }

  recordSuccess() {
    this.consecutiveFailures = 0;
    this.lastSuccessTime = Date.now();
    this.lastActivityTime = Date.now();
    this.notifyListeners();
  }

  reset() {
    this.consecutiveFailures = 0;
    this.lastSuccessTime = Date.now();
    this.lastActivityTime = Date.now();
    this.notifyListeners();
  }

  getConsecutiveFailures(): number {
    return this.consecutiveFailures;
  }

  getLastSuccessTime(): number {
    return this.lastSuccessTime;
  }

  getLastActivityTime(): number {
    return this.lastActivityTime;
  }

  isInErrorState(): boolean {
    return this.consecutiveFailures >= this.failureThreshold;
  }

  subscribe(callback: ErrorCallback): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  private notifyListeners() {
    this.listeners.forEach((callback) => callback(this.consecutiveFailures));
  }

  cleanup() {
    this.activityListeners.forEach((cleanup) => cleanup());
    this.activityListeners = [];
  }
}

export const errorTracker = new ErrorTracker();

